import { useEffect, useMemo, useState } from "react";
import { api, formatINR, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Briefcase, IndianRupee, Package, Calendar as CalendarIcon, Plus, Edit, Trash2, Save, Loader2, UserCog, CalendarDays, Star } from "lucide-react";
import { ProductForm } from "@/pages/AdminProducts";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{label}</div>
          <div className="font-display text-3xl font-bold">{value}</div>
        </div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#E5F0F0] text-teal">
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Rate Calendar sub-panel ---------------- */
function isoDate(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function firstOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

function RateCalendar({ product }) {
  const [monthAnchor, setMonthAnchor] = useState(() => firstOfMonth(new Date()));
  const [entries, setEntries] = useState({}); // date -> rate obj
  const [selected, setSelected] = useState(null); // date string
  const [draft, setDraft] = useState({ adult_price: "", child_price: "", available_seats: "", blackout: false, note: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const from = isoDate(monthAnchor);
  const to = isoDate(addDays(monthAnchor, daysInMonth(monthAnchor) - 1));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products/${product.id}/rates`, { params: { from, to } });
      const map = {};
      (data || []).forEach((r) => { map[r.date] = r; });
      setEntries(map);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [product?.id, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDay = (dateStr) => {
    const e = entries[dateStr];
    setSelected(dateStr);
    setDraft({
      adult_price: e?.adult_price ?? product.adult_price ?? "",
      child_price: e?.child_price ?? product.child_price ?? "",
      available_seats: e?.available_seats ?? "",
      blackout: !!e?.blackout,
      note: e?.note || "",
    });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/products/${product.id}/rates`, {
        entries: [{
          date: selected,
          adult_price: draft.adult_price === "" ? null : Number(draft.adult_price),
          child_price: draft.child_price === "" ? null : Number(draft.child_price),
          available_seats: draft.available_seats === "" ? null : Number(draft.available_seats),
          blackout: !!draft.blackout,
          note: draft.note || null,
        }],
      });
      toast.success(`Saved rate for ${selected}`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async () => {
    if (!selected) return;
    if (!window.confirm(`Reset ${selected} to default pricing?`)) return;
    try {
      await api.delete(`/products/${product.id}/rates/${selected}`);
      toast.success("Reset to default");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const grid = useMemo(() => {
    const startDow = new Date(monthAnchor).getDay(); // 0 = Sun
    const total = daysInMonth(monthAnchor);
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    return cells;
  }, [monthAnchor]);

  const monthLabel = monthAnchor.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const todayStr = isoDate(new Date());

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-display text-lg">{product.title}</div>
          <div className="text-xs text-muted-foreground">Default: {formatINR(product.adult_price)}/adult · {formatINR(product.child_price)}/child</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setMonthAnchor(firstOfMonth(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1)))} data-testid="cal-prev">‹ Prev</Button>
          <div className="min-w-[10rem] text-center font-semibold" data-testid="cal-month-label">{monthLabel}</div>
          <Button variant="outline" size="sm" onClick={() => setMonthAnchor(firstOfMonth(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1)))} data-testid="cal-next">Next ›</Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-muted-foreground">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1" data-testid="rate-calendar-grid">
        {grid.map((day, idx) => {
          if (!day) return <div key={idx} />;
          const dateStr = `${monthAnchor.getFullYear()}-${String(monthAnchor.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const e = entries[dateStr];
          const isPast = dateStr < todayStr;
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selected;
          return (
            <button
              key={dateStr}
              onClick={() => !isPast && openDay(dateStr)}
              disabled={isPast}
              data-testid={`cal-day-${dateStr}`}
              className={`min-h-[76px] p-2 rounded-lg border text-left text-xs transition-all ${
                isPast ? "opacity-40 cursor-not-allowed border-border bg-muted/30" :
                isSelected ? "border-teal ring-2 ring-teal bg-secondary/40" :
                e?.blackout ? "border-red-300 bg-red-50 dark:bg-red-950/20" :
                e ? "border-teal/60 bg-secondary/40" : "border-border hover:border-teal hover:bg-secondary/30"
              }`}
            >
              <div className={`font-semibold ${isToday ? "text-teal" : ""}`}>{day}</div>
              {e?.blackout ? (
                <div className="text-red-600 font-semibold mt-1">Blackout</div>
              ) : e?.adult_price != null ? (
                <div className="text-teal font-medium mt-1">₹{e.adult_price}</div>
              ) : (
                <div className="text-muted-foreground mt-1">default</div>
              )}
              {e?.available_seats != null && !e.blackout && (
                <div className="text-[10px] text-muted-foreground">{e.available_seats} seats</div>
              )}
            </button>
          );
        })}
      </div>

      {loading && <div className="text-center text-muted-foreground text-sm">Loading rates…</div>}

      {selected && (
        <Card className="p-5 border-teal" data-testid="rate-editor">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display text-lg">Edit rate — {selected}</div>
              <div className="text-xs text-muted-foreground">Leave a field blank to use product default.</div>
            </div>
            <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground text-sm">Close</button>
          </div>

          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <Label>Adult ₹</Label>
              <Input type="number" value={draft.adult_price} onChange={(e) => setDraft({ ...draft, adult_price: e.target.value })} placeholder={String(product.adult_price)} data-testid="rate-adult" />
            </div>
            <div>
              <Label>Child ₹</Label>
              <Input type="number" value={draft.child_price} onChange={(e) => setDraft({ ...draft, child_price: e.target.value })} placeholder={String(product.child_price || 0)} data-testid="rate-child" />
            </div>
            <div>
              <Label>Available seats</Label>
              <Input type="number" value={draft.available_seats} onChange={(e) => setDraft({ ...draft, available_seats: e.target.value })} placeholder="unlimited" data-testid="rate-seats" />
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={draft.blackout} onCheckedChange={(v) => setDraft({ ...draft, blackout: v })} id="blackout" data-testid="rate-blackout" />
              <Label htmlFor="blackout" className="cursor-pointer">Blackout (no bookings)</Label>
            </div>
            <div className="sm:col-span-4">
              <Label>Note (internal)</Label>
              <Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Peak weekend, holiday surcharge, etc." />
            </div>
          </div>

          <div className="flex justify-between mt-4">
            {entries[selected] ? (
              <Button variant="ghost" onClick={removeEntry} className="text-destructive" data-testid="rate-reset">
                <Trash2 className="w-4 h-4 mr-1" /> Reset to default
              </Button>
            ) : <span />}
            <Button onClick={save} disabled={saving} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="rate-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save</>}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ---------------- Profile tab ---------------- */
function ProfileTab() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/vendor/profile").then((r) => setProfile(r.data)).catch(() => setProfile({}));
  }, []);

  const set = (k, v) => setProfile({ ...profile, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/vendor/profile", {
        business_name: profile.business_name,
        gstin: profile.gstin,
        pan: profile.pan,
        phone: profile.phone,
        address: profile.address,
        logo_url: profile.logo_url,
        about: profile.about,
      });
      setProfile(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;

  return (
    <Card className="p-6 space-y-4" data-testid="vendor-profile">
      <h3 className="font-display text-xl">Business profile</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Business name</Label><Input value={profile.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="vp-business-name" /></div>
        <div><Label>Contact phone</Label><Input value={profile.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="vp-phone" /></div>
        <div><Label>GSTIN</Label><Input value={profile.gstin || ""} onChange={(e) => set("gstin", e.target.value)} placeholder="36AAACH7409R1Z2" data-testid="vp-gstin" /></div>
        <div><Label>PAN</Label><Input value={profile.pan || ""} onChange={(e) => set("pan", e.target.value)} placeholder="AAACH7409R" data-testid="vp-pan" /></div>
        <div className="sm:col-span-2"><Label>Address</Label><Input value={profile.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="vp-address" /></div>
        <div className="sm:col-span-2"><Label>About your business</Label><Textarea rows={3} value={profile.about || ""} onChange={(e) => set("about", e.target.value)} placeholder="Tell customers about your services, USPs, awards, etc." data-testid="vp-about" /></div>
        <div className="sm:col-span-2"><Label>Logo URL (or upload via Admin Settings)</Label><Input value={profile.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} data-testid="vp-logo" /></div>
      </div>
      <div className="flex items-center justify-between border-t border-border pt-4">
        <div className="text-sm">
          <span className="text-muted-foreground">Status: </span>
          <Badge className={profile.status === "approved" ? "bg-teal text-white" : "bg-muted"}>{profile.status || "pending"}</Badge>
        </div>
        <Button onClick={save} disabled={saving} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="vp-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save profile</>}
        </Button>
      </div>
    </Card>
  );
}

/* ---------------- Main ---------------- */
export default function VendorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ products: 0, bookings: 0, revenue: 0 });
  const [products, setProducts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [rateProduct, setRateProduct] = useState(null);

  const loadAll = () => {
    api.get("/vendor/stats").then((r) => setStats(r.data));
    api.get("/vendor/products").then((r) => setProducts(r.data));
    api.get("/vendor/bookings").then((r) => setBookings(r.data));
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (rateProduct && !products.find((p) => p.id === rateProduct.id)) {
      setRateProduct(null);
    }
  }, [products, rateProduct]);

  const createProduct = async (data) => {
    try {
      await api.post("/products", data);
      toast.success("Listing submitted — pending admin approval");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Create failed");
      throw err;
    }
  };

  const updateProduct = async (data) => {
    try {
      await api.put(`/vendor/products/${editing.id}`, data);
      toast.success("Listing updated");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
      throw err;
    }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This will also clear its rate calendar.`)) return;
    try {
      await api.delete(`/vendor/products/${p.id}`);
      toast.success("Deleted");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const bookingCount = bookings.length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="vendor-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-teal text-white flex items-center justify-center">
            <Briefcase className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl">Vendor Dashboard</h1>
            <p className="text-muted-foreground text-sm">Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""} · Manage listings, rates & bookings</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <StatCard icon={Package} label="My Listings" value={stats.products} testid="stat-listings" />
        <StatCard icon={CalendarIcon} label="Bookings" value={stats.bookings} testid="stat-bookings" />
        <StatCard icon={IndianRupee} label="Revenue" value={formatINR(stats.revenue || 0)} testid="stat-revenue" />
      </div>

      <Tabs defaultValue="bookings">
        <TabsList className="w-full justify-start bg-white border border-border flex-wrap h-auto">
          <TabsTrigger value="bookings" data-testid="vendor-tab-bookings"><CalendarIcon className="w-4 h-4 mr-1.5" />Booking Inbox <Badge variant="secondary" className="ml-2">{bookingCount}</Badge></TabsTrigger>
          <TabsTrigger value="products" data-testid="vendor-tab-products"><Package className="w-4 h-4 mr-1.5" />My Listings</TabsTrigger>
          <TabsTrigger value="rates" data-testid="vendor-tab-rates"><CalendarDays className="w-4 h-4 mr-1.5" />Rate Calendar</TabsTrigger>
          <TabsTrigger value="profile" data-testid="vendor-tab-profile"><UserCog className="w-4 h-4 mr-1.5" />Profile</TabsTrigger>
        </TabsList>

        {/* Bookings */}
        <TabsContent value="bookings">
          <div className="bg-white border border-border rounded-2xl overflow-hidden mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead><TableHead>Product</TableHead><TableHead>Customer</TableHead><TableHead>Travel</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((b) => (
                  <TableRow key={b.id} data-testid={`booking-row-${b.reference_no}`}>
                    <TableCell className="font-mono text-xs">{b.reference_no}</TableCell>
                    <TableCell>{b.product_title}</TableCell>
                    <TableCell>
                      <div className="text-sm">{b.contact_name}</div>
                      <div className="text-xs text-muted-foreground">{b.contact_phone}</div>
                    </TableCell>
                    <TableCell>{b.travel_date}</TableCell>
                    <TableCell className="text-saffron font-medium">{formatINR(b.total_amount)}</TableCell>
                    <TableCell><Badge>{b.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {bookings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-12">No bookings yet for your listings</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Listings CRUD */}
        <TabsContent value="products">
          <div className="flex justify-between items-center mt-4 mb-3 flex-wrap gap-2">
            <p className="text-sm text-muted-foreground">New / edited listings enter <b>pending</b> state until admin approval.</p>
            <Sheet open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
              <SheetTrigger asChild>
                <Button onClick={() => setEditing({})} className="bg-saffron hover:bg-saffron-dark text-white" data-testid="vendor-add-product">
                  <Plus className="w-4 h-4 mr-2" /> Add Listing
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="font-display">{editing?.id ? "Edit listing" : "New listing"}</SheetTitle>
                  <SheetDescription>Fill all required fields. Once submitted, admin will review before it goes live.</SheetDescription>
                </SheetHeader>
                {editing !== null && (
                  <ProductForm
                    initial={editing}
                    onSubmit={editing.id ? updateProduct : createProduct}
                    onCancel={() => setEditing(null)}
                  />
                )}
              </SheetContent>
            </Sheet>
          </div>

          <div className="bg-white border border-border rounded-2xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`vendor-prod-row-${p.slug}`}>
                    <TableCell>
                      <div className="font-medium">{p.title}</div>
                      <div className="text-xs text-muted-foreground">{p.slug}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{p.category}{p.sub_category && ` · ${p.sub_category}`}</Badge></TableCell>
                    <TableCell>{formatINR(p.adult_price)}</TableCell>
                    <TableCell><span className="flex items-center gap-1 text-sm"><Star className="w-3.5 h-3.5 fill-saffron text-saffron" />{p.rating?.toFixed(1)} ({p.review_count || 0})</span></TableCell>
                    <TableCell>
                      <Badge className={p.status === "active" ? "bg-teal text-white" : p.status === "pending" ? "bg-amber-500 text-white" : ""}>
                        {p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setRateProduct(p)} title="Rate calendar" data-testid={`vendor-rates-${p.slug}`}>
                          <CalendarDays className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`vendor-edit-${p.slug}`}><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => removeProduct(p)} className="text-destructive" data-testid={`vendor-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/${p.slug}`)} title="Preview">👁</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                      No listings yet. Click <b>Add Listing</b> to submit your first product for approval.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Rate Calendar */}
        <TabsContent value="rates">
          <Card className="p-5 mt-4">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <div>
                <h3 className="font-display text-xl">Rate Calendar</h3>
                <p className="text-sm text-muted-foreground">Set date-wise prices, seat availability & blackout dates for each listing.</p>
              </div>
              <div className="min-w-[280px]">
                <Select
                  value={rateProduct?.id || "_none"}
                  onValueChange={(v) => setRateProduct(products.find((p) => p.id === v) || null)}
                >
                  <SelectTrigger data-testid="rate-product-select"><SelectValue placeholder="Choose a listing" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Choose a listing —</SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {rateProduct ? (
              <RateCalendar product={rateProduct} />
            ) : (
              <div className="text-center text-muted-foreground py-16">
                {products.length === 0
                  ? "Add a listing first from My Listings tab."
                  : "Select a listing above to open its rate calendar."}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Profile */}
        <TabsContent value="profile" className="mt-4">
          <ProfileTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
